import { assert } from '../../lib/assert-sidecar'
import type { IIJ, IXY } from '../../lib/distanceUtil'
import { rando } from '../../lib/rando-sidecar'
import { common } from '../../main/common-sidecar'

import { voyageCamera } from './voyageCamera-sidecar'
import { voyageSystems } from './voyageSystems-sidecar'
import { voyageSystems_cryo } from './voyageSystems_cryo-sidecar'
import { EVoyageTile } from './voyageTileData'
import type { IVoyageTilemapPart } from './voyageTilemapPart'
import { voyageTilemapPart } from './voyageTilemapPart-sidecar'

export interface IVoyageTilemap {
  parts: IVoyageTilemapPart[]
  width: number
  height: number
  elevators: { [k: string]: IVoyageTilemapPart[] }
  systems: { [k: string]: IVoyageTilemapPart }
  breaches: IVoyageTilemapPart[]
}

let _obj: IVoyageTilemap | null = null
export function get() {
  assert.notNil(_obj, 'tilemap not created')
  return _obj
}

export const spotSize = 32

export function create(): IVoyageTilemap {
  let obj: IVoyageTilemap = {
    parts: [],
    width: 0,
    height: 0,
    // x: 0,
    // y: 0,
    elevators: {},
    systems: {},
    breaches: [],
  }
  setSize(obj, 100, 100)
  _obj = obj
  return obj
}

function setSize(obj: IVoyageTilemap, width: number, height: number) {
  obj.width = width
  obj.height = height
  obj.parts = []

  // TODO: kill existing?
  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      let gs = voyageTilemapPart.create(
        i,
        j,
        i * spotSize + spotSize / 2,
        j * spotSize + spotSize / 2,
      )
      obj.parts.push(gs)
    }
  }
}

export function inRange(i: number, j: number) {
  let obj = get()
  return i >= 0 && i < obj.width && j >= 0 && j < obj.height
}

export function getUnsafe(i: number, j: number) {
  let obj = get()
  assert.t(inRange(i, j), 'not in range', i, j)
  return obj.parts[i + j * obj.width]
}

export function getSafe(i: number, j: number) {
  let obj = get()
  if (!inRange(i, j)) {
    return null
  }
  return obj.parts[i + j * obj.width]
}
export function getSafeIJ(iij: IIJ) {
  return getSafe(iij.i, iij.j)
}
export function getSafeXY(ixy: IXY) {
  let i = convertXtoI(ixy.x)
  let j = convertYtoJ(ixy.y)
  return getSafe(i, j)
}

export function getSafeOffset(iij: IIJ, oi: number, oj: number) {
  return getSafe(iij.i + oi, iij.j + oj)
}

export function update() {}

export function getIJAtMouse() {
  let s = common.getPhaserScene()
  let mx = s.input.mousePointer.x
  let my = s.input.mousePointer.y
  let i = convertSXtoI(mx)
  let j = convertSYtoJ(my)
  return { i, j }
}

export function convertXtoI(_x: number) {
  let i = Math.floor(_x / spotSize)
  return i
}

export function convertYtoJ(_y: number) {
  let j = Math.floor(_y / spotSize)
  return j
}

export function convertSYtoJ(_y: number) {
  let cam = voyageCamera.get()
  let zoom = voyageCamera.getZoom()
  let cy = _y / zoom + cam.cameraY / zoom
  let j = Math.floor(cy / spotSize)
  return j
}

export function convertSXtoI(_x: number) {
  let cam = voyageCamera.get()
  let zoom = voyageCamera.getZoom()
  let cx = _x / zoom + cam.cameraX / zoom
  let i = Math.floor(cx / spotSize)
  return i
}

export function convertIJtoXY(i: number, j: number) {
  let x = convertItoX(i)
  let y = convertJtoY(j)
  return { x, y }
}
export function convertItoX(i: number) {
  let x = i * spotSize + spotSize / 2
  return x
}
export function convertJtoY(j: number) {
  let y = j * spotSize + spotSize / 2
  return y
}

export function addGameElevator(c: IVoyageTilemapPart) {
  let obj = get()
  assert.eq(
    c.text3,
    c.text3.toLowerCase(),
    'elevators must be lowercase',
    c.text3,
  )
  if (!obj.elevators[c.text3]) {
    obj.elevators[c.text3] = []
  }
  obj.elevators[c.text3].push(c)
}

export function getElevators(tag: string) {
  let obj = get()
  let els = obj.elevators[tag]
  assert.notNil(els, 'no elevator with tag', tag)
  return els
}

export function addGameSystem(c: IVoyageTilemapPart) {
  let obj = get()
  let key = c.text3
  addGameSystem2(c, key)
}

export function addGameSystem2(c: IVoyageTilemapPart, key: string) {
  let obj = get()

  assert.eq(key, key.toLowerCase(), 'systems must be lowercase', key)

  assert.notEmptyString(key, 'key is empty!')
  // assert.nil(obj.systems[key], 'duplicate system', '' + key)
  if (obj.systems[key] != null) {
    console.warn('duplicate system', key, obj.systems[key])
  }
  voyageSystems.init(c)
  obj.systems[key] = c
}

export function getSystem(tag: string) {
  let obj = get()
  let sys = obj.systems[tag]
  assert.notNil(sys, 'no system with tag', tag)
  return sys
}
export function getSystemSafe(tag: string) {
  let obj = get()
  let sys = obj.systems[tag]
  //assert.notNil(sys, 'no system with tag', tag)
  return sys || null
}

export function getRandomFloor() {
  let tm = get()
  let width = tm.width
  let height = tm.height

  let options: IVoyageTilemapPart[] = []

  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      let t = getUnsafe(i, j)
      if (t.tile2.type === EVoyageTile.Floor) {
        options.push(t)
      }
    }
  }

  return rando.sample(options)
}
